import { defineStore } from 'pinia';
import type { Organisation } from '@/api/model/Organisation';
import { DateTime } from 'luxon';

interface State {
    isFetchingOrganisations: boolean;
    organisations: Organisation[];
    current_organisation: Organisation;
    loading: boolean;
}

const ORG_STORAGE_KEY = 'organisation';

export const useOrganisationStore = defineStore('organisation', {
    state: (): State => ({
        isFetchingOrganisations: false,
        organisations: [],
        current_organisation: {
            id: 0,
            name: '',
            logo: '',
            public_id: localStorage.getItem(ORG_STORAGE_KEY) || 'unknown',
            base_currency: null,
            vat_number: null,
            company_registration_number: null,
            registered_office_address: null,
            created_at: DateTime.local(),
            updated_at: DateTime.local(),
            oldestOrderDate: null,
        },
        loading: false,
    }),
    getters: {
        currentOrganisationId(): number {
            return this.current_organisation.id;
        },
    },
    actions: {
        setOrganisations(organisations: Organisation[]): Organisation[] {
            this.organisations = organisations;

            const stored = localStorage.getItem(ORG_STORAGE_KEY) ?? 'unknown';
            const organisation = this.findOrganisationByPublicId(stored) ?? organisations[0];
            this.setCurrentOrganisation(organisation);
            return organisations;
        },

        setCurrentOrganisationPublicId(publicId: string): void {
            localStorage.setItem(ORG_STORAGE_KEY, publicId);
            const organisation = this.findOrganisationByPublicId(publicId);
            if (organisation) {
                this.setCurrentOrganisation(organisation);
            }
        },
        setCurrentOrganisation(org: Organisation | undefined): void {
            this.current_organisation = org ?? {
                id: 0,
                name: 'Unknown',
                public_id: 'unknown',
                logo: 'Unknown',
                base_currency: null,
                vat_number: null,
                company_registration_number: null,
                registered_office_address: null,
                created_at: DateTime.local(),
                updated_at: DateTime.local(),
                oldestOrderDate: null,
            };
            localStorage.setItem(ORG_STORAGE_KEY, this.current_organisation.public_id);

            const route = this.router.currentRoute.value;
            if (route.params.organisation && route.params.organisation !== this.current_organisation.public_id) {
                this.router.push({
                    name: route.name,
                    params: {
                        ...route.params,
                        organisation: this.current_organisation.public_id,
                    },
                    query: route.query,
                });
            }
        },

        findOrganisationByPublicId(publicId: string): Organisation | undefined {
            return this.organisations.find((org: Organisation) => org.public_id === publicId);
        },
    },
});
